import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { SnackBarService } from 'src/app/services/snackBar/snack-bar.service';
import { TicketsService } from 'src/app/services/tickets/tickets.service';
import { MatDialog } from '@angular/material/dialog';
import { PhotoCaptureComponent } from '../nuevo-caso/photo-capture/photo-capture.component';

@Component({
  selector: 'app-seguimiento',
  templateUrl: './seguimiento.component.html',
  styleUrls: ['./seguimiento.component.scss'],
})
export class SeguimientoComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  private destroyed$ = new Subject<void>();

  id: any = null;

  seguimientos: any = [];

  textoSeguimiento = '';

  archivosAdjuntos: File[] = [];

  mostrarMenuAdjuntar = false;

  loading = false;

  @ViewChild('mensajesContainer', { static: false }) mensajesContainer:
    | ElementRef
    | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ticketsSvc: TicketsService,
    private snackBarService: SnackBarService,
    private translocoService: TranslocoService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.id = +params['id'];
      console.log('ID capturado:', this.id);
      this.loadSeguimientos(this.id);
    });

    // Cerrar menú al hacer clic fuera
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.adjuntar-container')) {
        this.mostrarMenuAdjuntar = false;
      }
    });
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  scrollToBottom(): void {
    if (this.mensajesContainer && this.mensajesContainer.nativeElement) {
      this.mensajesContainer.nativeElement.scrollTop =
        this.mensajesContainer.nativeElement.scrollHeight;
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  showSnackBarError(mensaje: string) {
    this.snackBarService.openSnackBarError(mensaje);
  }

  goBack() {
    this.router.navigate([`/caso/${this.id}`]);
  }

  toggleAdjuntarMenu() {
    this.mostrarMenuAdjuntar = !this.mostrarMenuAdjuntar;
  }

  seleccionarArchivo() {
    this.mostrarMenuAdjuntar = false;
    // Simular click en el input de archivo
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  tomarFoto() {
    this.mostrarMenuAdjuntar = false;
    this.openPhotoCaptureDialog();
  }

  openPhotoCaptureDialog() {
    const dialogRef = this.dialog.open(PhotoCaptureComponent, {
      data: {
        onlyPhoto: true,
        multipleSelectionEnabled: false,
      },
      maxWidth: '100%',
      maxHeight: '100%',
      panelClass: 'custom-modal',
    });

    dialogRef
      .afterClosed()
      .subscribe(
        (result: { file: File; base64: string; type: 'photo' | 'video' }[]) => {
          if (result && result.length > 0) {
            // Procesar las fotos capturadas
            result.forEach(item => {
              // Ahora el PhotoCaptureComponent debería devolver correctamente el File object
              this.archivosAdjuntos.push(item.file);
            });
          }
        }
      );
  }

  base64ToFile(base64: string, filename: string, mimeType: string): File {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return new File([blob], filename, { type: mimeType });
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        // Verificar el tamaño del archivo (máximo 10MB)
        if (file.size > 10 * 1024 * 1024) {
          this.showSnackBarError('El archivo no puede ser mayor a 10 MB');
          continue;
        }
        this.archivosAdjuntos.push(file);
      }
    }
    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
    event.target.value = '';
  }

  eliminarArchivo(index: number) {
    this.archivosAdjuntos.splice(index, 1);
  }

  crearNuevoSeguimiento() {
    const contenidoMensaje = this.textoSeguimiento;

    if (this.archivosAdjuntos.length > 0) {
      this.loading = true;
      console.log('[seguimiento] Subiendo', this.archivosAdjuntos.length, 'adjuntos...');
      this.subirArchivosAdjuntos()
        .pipe(take(1))
        .subscribe({
          next: (adjuntosSubidosIds) => {
            console.log('[seguimiento] Adjuntos subidos, IDs:', adjuntosSubidosIds);
            this.crearSeguimiento(contenidoMensaje, adjuntosSubidosIds);
          },
          error: (err) => {
            console.error('[seguimiento] Error al subir adjuntos:', err);
            this.showSnackBarError(
              this.translocoService.translate('issue.error.uploadAttachment')
            );
            this.loading = false;
          },
        });
    } else {
      console.log('[seguimiento] Sin adjuntos, creando seguimiento directamente');
      this.crearSeguimiento(contenidoMensaje, []);
      this.loading = false;
    }
  }

  crearSeguimiento(contenidoMensaje: string, adjuntosSubidosIds: number[]) {
    // Crear el seguimiento con el contenido modificado
    this.ticketsSvc
      .crearSeguimiento(this.id, contenidoMensaje, adjuntosSubidosIds)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: async (v) => {
          console.log(v);
          this.loading = false;
          this.textoSeguimiento = '';
          this.archivosAdjuntos = [];
          this.loadSeguimientos(this.id);
        },
        error: (e) => {
          console.log(e);
          this.loading = false;
          this.showSnackBarError(
            this.translocoService.translate(
              'issue.tracking.error.createTracking'
            )
          );
        },
      });
  }

  /**
   * Uploads all attached files and returns an observable that emits the list of uploaded document IDs
   * once all uploads have completed successfully.
   */
  subirArchivosAdjuntos(): Observable<number[]> {
    if (this.archivosAdjuntos.length === 0) {
      return of([]);
    }
    const uploads$ = this.archivosAdjuntos.map((archivo, index) =>
      from(this.convertirArchivoABase64(archivo)).pipe(
        switchMap((base64) => {
          const tipoAdjunto = this.obtenerTipoAdjunto(archivo);
          console.log('[seguimiento] Subiendo adjunto', index + 1, '/', this.archivosAdjuntos.length, archivo.name);
          return this.ticketsSvc.uploadAdjunto(
            this.id,
            tipoAdjunto,
            archivo.name,
            base64
          );
        }),
        map((response) => {
          const id = response?.ID ?? response?.id;
          console.log('[seguimiento] Adjunto subido, response:', response, '-> id:', id);
          return id as number;
        })
      )
    );
    return forkJoin(uploads$).pipe(takeUntil(this.destroyed$));
  }

  convertirArchivoABase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remover el prefijo "data:image/jpeg;base64," o similar
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  obtenerTipoAdjunto(file: File): number {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    // Tipos de adjunto según el sistema (pueden variar según la implementación)
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '')) {
      return 1; // Imagen
    } else if (['mp4', 'mov', 'avi', 'wmv', 'mkv', 'webm'].includes(extension || '')) {
      return 2; // Video
    } else if (['pdf'].includes(extension || '')) {
      return 3; // PDF
    } else if (['doc', 'docx'].includes(extension || '')) {
      return 4; // Documento
    } else {
      return 5; // Otro tipo de archivo
    }
  }

  obtenerIconoArchivo(file: File): string {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '')) {
      return 'image';
    } else if (['mp4', 'mov', 'avi', 'wmv', 'mkv', 'webm'].includes(extension || '')) {
      return 'videocam';
    } else if (['pdf'].includes(extension || '')) {
      return 'picture_as_pdf';
    } else if (['doc', 'docx'].includes(extension || '')) {
      return 'description';
    } else if (['txt'].includes(extension || '')) {
      return 'text_snippet';
    } else if (['zip', 'rar', '7z'].includes(extension || '')) {
      return 'folder_zip';
    } else {
      return 'attach_file';
    }
  }

  formatearTamano(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }


	descargarAdjunto(adjunto: any) {
		this.ticketsSvc
			.downloadAdjunto(this.id, adjunto.ID)
			.pipe(take(1))
			.subscribe(
				(data: any) => {
					try {
						const enlaceDescarga = document.createElement("a");
						enlaceDescarga.href = data;
						enlaceDescarga.download = adjunto.filename; // Nombre del archivo a descargar
						enlaceDescarga.click();

						URL.revokeObjectURL(enlaceDescarga.href); // Liberar el objeto URL
					} catch (error) {
						console.error("Error al descargar el archivo:", error);
					}
				},
				(err) => {
					this.showSnackBarError(err);
				}
			);
	}

	getIconFile(adjunto: any) {
		let icon = "insert_drive_file";
		const extension = adjunto.filename.split('.').pop().toLowerCase();
		switch (extension) {
			case "xls":
			case "xlsx":
				icon = "grid_on";
				break;
			case "jpg":
			case "jpeg":
			case "png":
				icon = "image";
				break;
			case "pdf":
				icon = "picture_as_pdf";
				break;
			case "webm":
			case "mp4":
			case "mkv":
			case "avi":
				icon = "videocam";
				break;
			case "doc":
			case "docx":
				icon = "description";
				break;
			case "txt":
				icon = "text_snippet";
				break;
			case "zip":
			case "rar":
			case "7z":
				icon = "folder_zip";
				break;
			default:
				break;
		}
		return icon;
	}

  loadSeguimientos(ticket: any) {
    this.ticketsSvc
      .seguimientosTicket(ticket)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (v) => {
          console.log(v);
          this.seguimientos = v;
          this.scrollToBottom();
        },
        error: (e) => {
          console.log(e);
          this.showSnackBarError(
            this.translocoService.translate(
              'issue.tracking.error.loadTrackings'
            )
          );
          this.router.navigate(['/casos']);
        },
      });
  }
}
